// plugins/anonymous-id.client.ts
export default defineNuxtPlugin(() => {
  const ROTATION_MINUTES = 30

  // --- Types ---
  type CookieName = 'anonymous-id'
  type StorageKey = 'anonymous-id-last-rotated' | 'anonymous-id-sync'

  // --- Cookie Helpers ---
  const getCookie = (name: CookieName): string | null => {
    const match = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${name}=`))

    return match ? match.split('=')[1] : null
  }

  const setCookie = (name: CookieName, value: string, minutes: number): void => {
    const expires = new Date(Date.now() + minutes * 60_000).toUTCString()
    document.cookie = `${name}=${value}; path=/; expires=${expires}; SameSite=Lax`
  }

  // --- Strong Entropy Generator ---
  const generateAnonymousId = (): string => {
    const extra = crypto.getRandomValues(new Uint32Array(1))[0].toString(16)
    return `${crypto.randomUUID()}-${extra}`
  }

  // --- Load or Create Anonymous ID ---
  let anonymousId: string | null = getCookie('anonymous-id')

  if (!anonymousId) {
    anonymousId = generateAnonymousId()
    setCookie('anonymous-id', anonymousId, ROTATION_MINUTES)
  }

  // --- Rotation Logic ---
  const rotationKey: StorageKey = 'anonymous-id-last-rotated'
  const lastRotated = Number(localStorage.getItem(rotationKey) || 0)
  const now = Date.now()

  if (now - lastRotated > ROTATION_MINUTES * 60_000) {
    anonymousId = generateAnonymousId()
    setCookie('anonymous-id', anonymousId, ROTATION_MINUTES)
    localStorage.setItem(rotationKey, now.toString())
  }

  // --- Cross‑Tab Sync ---
  const syncKey: StorageKey = 'anonymous-id-sync'
  localStorage.setItem(syncKey, anonymousId!)

  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key === syncKey && event.newValue) {
      document.cookie = `anonymous-id=${event.newValue}; path=/; SameSite=Lax`
    }
  })
})
